import React, { useEffect, useState } from "react";
import { Box, Stack, Dialog, TextField, CircularProgress } from "@mui/material";
import ErrorMsg from "../ErrorMsg";
import BtnPrimary from "../PrimaryButton";
import BtnSecondary from "../SecondaryButton";
import { tokens } from "../../theme";

/**
 * Reopening a closed cycle or a finalized review.
 *
 * The reason is required, and the server enforces it too. An appraisal that can
 * be unlocked without anyone saying why is not a record of anything — and this
 * is the one action in the module that changes what has already been decided,
 * so it should feel deliberate rather than like another button.
 *
 * `consequence` spells out what will and will not change. "Reopened" reads as
 * "everything is editable again", and for a cycle that is not true: the reviews
 * inside it stay finalized until each is reopened on its own.
 */
const ReopenDialog = ({ open, title, consequence, confirmLabel = "Reopen", onClose, onConfirm }) => {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  // The reason is a field, so its failure belongs on the field.
  const [reasonError, setReasonError] = useState("");

  useEffect(() => {
    if (open) { setReason(""); setError(""); setReasonError(""); setBusy(false); }
  }, [open]);

  const submit = async () => {
    if (!reason.trim()) {
      setReasonError("A reason is required — it is what the record will show.");
      return;
    }
    setBusy(true);
    const res = await onConfirm(reason.trim());
    setBusy(false);
    if (res?.success === false) setError(res?.message || "Could not reopen");
    else onClose();
  };

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} PaperProps={{ sx: { borderRadius: "12px", width: "min(30rem,94vw)" } }}>
      <Box sx={{ p: "1.5rem" }}>
        <Box sx={{ fontSize: "1rem", fontWeight: 600, color: tokens.text, mb: "0.5rem" }}>{title}</Box>
        {consequence && (
          <Box sx={{ fontSize: "0.8125rem", color: tokens.textMuted, lineHeight: 1.5, mb: "1rem" }}>
            {consequence}
          </Box>
        )}

        <Box sx={{ fontSize: "0.8125rem", fontWeight: 500, color: tokens.textMuted, mb: "0.5rem" }}>
          Reason (recorded)
        </Box>
        <TextField
          fullWidth size="small" multiline minRows={2} autoFocus
          placeholder="e.g. closed a week early by mistake"
          value={reason}
          error={Boolean(reasonError)}
          onChange={(e) => { setReason(e.target.value); setReasonError(""); }}
          onBlur={() => setReasonError(reason.trim() ? "" : "A reason is required — it is what the record will show.")}
        />
        <ErrorMsg>{reasonError}</ErrorMsg>

        {error && <Box sx={{ fontSize: "0.8125rem", color: tokens.danger, mt: "0.75rem" }}>{error}</Box>}

        <Stack direction="row" justifyContent="flex-end" spacing={1.5} sx={{ mt: "1.5rem" }}>
          <BtnSecondary onClick={onClose} disabled={busy} sx={{ borderRadius: "8px" }}>
            Cancel
          </BtnSecondary>
          <BtnPrimary onClick={submit} disabled={busy} sx={{ borderRadius: "8px" }}>
            {busy ? <CircularProgress size={18} sx={{ color: "#fff" }} /> : confirmLabel}
          </BtnPrimary>
        </Stack>
      </Box>
    </Dialog>
  );
};

export default ReopenDialog;
